// Client-side mirror of the server's GameState. Built from the welcome's BMAP
// payload (initial terrain) and updated incrementally from snapshot deltas.

import { parseBMap } from '@bolo2k/bmap';
import {
  MAP_SIZE,
  type BotPersonality,
  type S2CSnapshot,
  type S2CWelcome,
} from '@bolo2k/shared';

export interface ClientTank {
  id: number;
  owner: number;
  alive: boolean;
  x: number;
  y: number;
  dir: number;
  speed: number;
  armour: number;
  shells: number;
  mines: number;
  trees: number;
  carriedPills: number;
  sightLen: number;
  kills: number;
  deaths: number;
  lgmStatus: number;
  lgmX: number;
  lgmY: number;
  inBoatId: number;
}

export interface ClientShell {
  id: number;
  x: number;
  y: number;
  dir: number;
}

export interface ClientBoat {
  id: number;
  riderTankId: number;
  x: number;
  y: number;
  dir: number;
  speed: number;
}

export interface ClientPillbox {
  id: number;
  owner: number;
  x: number;
  y: number;
  armour: number;
  /** 0 = on the map; otherwise tank.id of the carrier. Pills with
   *  carriedBy != 0 keep their slot in the status grid (so the player can
   *  still see the pill exists) but skip world-rendering. */
  carriedBy: number;
}

export interface ClientBase {
  id: number;
  owner: number;
  x: number;
  y: number;
  armour: number;
  shells: number;
  mines: number;
}

export interface ClientTankExplosion {
  id: number;
  x: number;
  y: number;
  kind: 'big' | 'small';
  ttl: number;
}

export interface ClientState {
  myOwner: number;
  myTankId: number;
  mapName: string;
  serverTick: number; // tick of last received snapshot
  terrain: Uint8Array; // 256x256 row-major, runtime byte (terrain | mine bit)
  /** Row-major tile indices whose terrain byte changed since the renderer
   *  last drew. applySnapshot appends one entry per applied terrain delta;
   *  Renderer.draw() drains the array (repaints each tile's 3×3
   *  neighbourhood in its chunked terrain cache, then truncates to length
   *  0). CONTRACT: exactly ONE Renderer may consume a given ClientState —
   *  a second consumer would always see an already-drained array. Other
   *  terrain observers (e.g. the minimap) key off `terrainVersion` instead. */
  terrainDirty: number[];
  tanks: ClientTank[];
  shells: ClientShell[];
  boats: ClientBoat[];
  pillboxes: ClientPillbox[];
  bases: ClientBase[];
  tankExplosions: ClientTankExplosion[];
  // Map<tankId, personality> for tanks driven by an autopilot brain.
  // Snapshot rebuilds this each frame; renderer reads it to label bot tanks.
  bots: Map<number, BotPersonality>;
  // Per-player alliance bitmaps (length MAX_PLAYERS). `wantAllies[i]` = bits
  // for slots player i has requested an alliance with. `allies[i]` = the
  // active mutual-pact bitmap. Both come from snapshots verbatim.
  wantAllies: number[];
  allies: number[];
  // Display name per player slot. Empty = slot unoccupied or human hasn't
  // sent their name yet — UIs fall back to playerLabel() below.
  playerNames: string[];
  /** Bumps on every snapshot. Chrome panels (minimap, middle/right columns,
   *  alliance panel) gate their per-frame draw on this so they only repaint when
   *  underlying state has actually changed, instead of redrawing at the 60 Hz
   *  rAF rate. The world canvas in renderer.ts still runs every frame because
   *  it interpolates/predicts smooth motion between snapshots. */
  version: number;
  /** Bumps only when the terrain grid changes (a snapshot carrying terrain
   *  deltas, or initial load). The minimap caches its expensive
   *  per-pixel terrain image and repaints it only when this changes — tank
   *  dots redraw on every `version` bump on top of the cache. */
  terrainVersion: number;
  /** Pillbox id the player is currently viewing through; 0 = viewing from
   *  the tank (default). Server is authoritative — this is the most recent
   *  echo. If the player asked to view through a pill that was then
   *  destroyed / captured / picked up, the next snapshot will carry
   *  viewPillId=0 and the local UI will snap to tank view. [Cheshire
   *  Brain.h `pillview`, GameFAQ "screen faded out..."] */
  viewPillId: number;
}

function decodeBase64(b64: string): Uint8Array {
  // Browser-friendly base64 decode (also works in Node via the global atob).
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function initFromWelcome(welcome: S2CWelcome): ClientState {
  const bmapBytes = decodeBase64(welcome.mapBmapBase64);
  const bmap = parseBMap(bmapBytes);
  return {
    myOwner: welcome.owner,
    myTankId: welcome.tankId,
    mapName: welcome.mapName,
    serverTick: welcome.serverTick,
    terrain: bmap.terrain, // already 256x256, runtime encoding
    terrainDirty: [],
    tanks: [],
    shells: [],
    boats: [],
    pillboxes: [],
    bases: [],
    tankExplosions: [],
    bots: new Map(),
    wantAllies: [],
    allies: [],
    playerNames: [],
    version: 0,
    terrainVersion: 0,
    viewPillId: 0,
  };
}

export function applySnapshot(state: ClientState, snap: S2CSnapshot): void {
  state.serverTick = snap.tick;
  state.tanks = snap.tanks;
  state.shells = snap.shells;
  state.boats = snap.boats;
  state.pillboxes = snap.pillboxes;
  state.bases = snap.bases;
  state.tankExplosions = snap.tankExplosions;
  state.bots.clear();
  for (const b of snap.bots) state.bots.set(b.tankId, b.personality);
  state.wantAllies = snap.wantAllies;
  state.allies = snap.allies;
  state.playerNames = snap.playerNames;
  state.viewPillId = snap.viewPillId;
  for (const [idx, byte] of snap.terrainDeltas) {
    if (idx < 0 || idx >= state.terrain.length) continue;
    if (state.terrain[idx] === byte) continue; // no-op delta — nothing to repaint
    state.terrain[idx] = byte;
    state.terrainDirty.push(idx);
  }
  state.version++;
  if (snap.terrainDeltas.length > 0) state.terrainVersion++;
  void MAP_SIZE; // referenced for documentation
}

export function findMyTank(state: ClientState): ClientTank | undefined {
  return state.tanks.find((t) => t.id === state.myTankId);
}

/** Display label for a player slot. Returns the server-side display name if
 *  set, otherwise the canonical `P0..P7` fallback used pre-Phase-13. */
export function playerLabel(state: ClientState, owner: number): string {
  const name = state.playerNames[owner];
  return name && name.length > 0 ? name : `P${owner}`;
}

/** Alliance-state classification for a pillbox, used by the middle-column
 *  pillbox status grid. Mirrors WinBolo's `pillsGetAllianceNum` (pillbox.c
 *  234-264): when a pillbox is `inTank` (being carried by a tank, i.e.
 *  `carriedBy !== 0` in our snapshot), it shows a tank-overlay variant
 *  (`pillTankGood/Allie/Evil`) so the player can see who's hauling it; when
 *  on the map, the regular alliance icon applies. */
export type PillStatusKind =
  | 'dead'
  | 'pillGood'
  | 'pillAllie'
  | 'pillNeutral'
  | 'pillEvil'
  | 'pillTankGood'
  | 'pillTankAllie'
  | 'pillTankEvil';
export function pillStatusKind(
  pill: { owner: number; armour: number; carriedBy: number },
  state: ClientState,
): PillStatusKind {
  // Carried pills keep their slot in the grid; classify by who's carrying
  // them (so the player can see "my own pill is in transit" vs "the enemy
  // is hauling a pill toward us"). Mirrors WinBolo's `inTank` branch which
  // returns the `pillTank*` overlay variant. Neutral carriers (which can't
  // exist in practice — only real players carry pills) fall back to the
  // plain neutral icon.
  if (pill.carriedBy !== 0) {
    const carrier = state.tanks.find((t) => t.id === pill.carriedBy);
    const carrierOwner = carrier?.owner ?? -1;
    if (state.myOwner < 0) return carrierOwner === 0xff ? 'pillNeutral' : 'pillTankEvil';
    if (carrierOwner === state.myOwner) return 'pillTankGood';
    if (carrierOwner === 0xff) return 'pillNeutral';
    if (carrierOwner >= 0 && isFriendlyToMe(state, carrierOwner)) return 'pillTankAllie';
    return 'pillTankEvil';
  }
  if (pill.armour === 0) return 'dead';
  if (state.myOwner < 0) return pill.owner === 0xff ? 'pillNeutral' : 'pillEvil';
  if (pill.owner === state.myOwner) return 'pillGood';
  if (pill.owner === 0xff) return 'pillNeutral';
  if (isFriendlyToMe(state, pill.owner)) return 'pillAllie';
  return 'pillEvil';
}

/** Alliance-state classification for a base. WinBolo's `basesGetStatusNum`
 *  (bases.c) marks a base "dead" when armour ≤ BASE_DEAD (9); otherwise it
 *  resolves alliance the same way as pillboxes. */
export type BaseStatusKind = 'dead' | 'baseGood' | 'baseAllie' | 'baseNeutral' | 'baseEvil';
export function baseStatusKind(
  base: { owner: number; armour: number },
  state: ClientState,
  baseDeadThreshold: number,
): BaseStatusKind {
  if (base.armour <= baseDeadThreshold) return 'dead';
  if (state.myOwner < 0) return base.owner === 0xff ? 'baseNeutral' : 'baseEvil';
  if (base.owner === state.myOwner) return 'baseGood';
  if (base.owner === 0xff) return 'baseNeutral';
  if (isFriendlyToMe(state, base.owner)) return 'baseAllie';
  return 'baseEvil';
}

/** Renderer-side friendly-to-me predicate. Mirrors `isFriendlyTo` in
 *  packages/sim/src/alliance.ts, but reads the snapshot's pre-derived active
 *  alliance bitmap instead of the sim's `playerWantAllies` (which the client
 *  doesn't carry — only the server computes the active mutual-pact bitmap
 *  and broadcasts it as `state.allies`).
 *
 *  Spectators (`myOwner < 0`) have no allegiance, so this returns false for
 *  every owner — every entity renders as "evil" from a spectator viewpoint.
 *  NEUTRAL_OWNER (0xff) is never friendly via this path; neutral pillboxes
 *  are hostile-to-all and neutral bases are handled by the renderer's own
 *  `kind === 'neutral'` branch. */
export function isFriendlyToMe(state: ClientState, owner: number): boolean {
  if (state.myOwner < 0) return false;
  if (owner === state.myOwner) return true;
  if (owner < 0 || owner === 0xff) return false;
  const myActive = state.allies[state.myOwner] ?? 0;
  return (myActive & (1 << owner)) !== 0;
}
